// DetailedInfo.js - API Integrated Version
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  AvatarGroup,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import {
  CalendarToday,
  Schedule,
  Person,
  AccountBalance,
  CheckCircle,
  Add,
  Event,
  Delete,
  TrendingUp
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';


import EndMeeting from './EndMeeting';
// Import your API functions
import { diaryAPI, formatters, handleAPIError } from './api';

const DetailedInfo = ({ onBack, selectedGroupId, selectedGroupIds }) => {
  const theme = useTheme();
  // State for dialogs
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, groupId: null, isScheduling: false });
  const [expensesDialog, setExpensesDialog] = useState({ open: false, groupId: null });
  const [advanceDialog, setAdvanceDialog] = useState({ open: false, groupId: null });

  // State for data
  const [groupsData, setGroupsData] = useState([]);
  const [availableOfficers, setAvailableOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endMeetingDialog, setEndMeetingDialog] = useState({ 
    open: false, 
    groupData: null, 
    advanceFromOffice: 0
  });
  

  // State for notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog form states
  const [rescheduleForm, setRescheduleForm] = useState({
    rescheduleDate: false,
    reassignOfficers: false,
    newDate: '',
    selectedOfficers: []
  });

  const [expensesList, setExpensesList] = useState([{ name: '', amount: '' }]);
  const [advanceAmount, setAdvanceAmount] = useState('');

  const canEndMeeting = (meetingDate, meetingStatus) => {
    return meetingDate && meetingStatus !== 'ended';
  };
  

  const handleEndMeeting = (groupData) => {
    // Get the requested advance loans (advance from office) for this group
    const advanceFromOffice = groupData.meetings[0]?.additionalFunds || 0; // additionalFunds = requested_advance_loans
    
    setEndMeetingDialog({ 
      open: true, 
      groupData,
      advanceFromOffice: advanceFromOffice
    });
  };
  
  const handleEndMeetingSubmit = async (endMeetingData) => {
    const { groupData } = endMeetingDialog;
    
    try {
      await diaryAPI.meetings.endMeeting(groupData.activeMeetingId, endMeetingData);
      
      // Refresh data
      await loadInitialData();
      
      setEndMeetingDialog({ open: false, groupData: null });
      
      showSnackbar('Meeting ended successfully!', 'success');
      
    } catch (err) {
      const errorMessage = handleAPIError(err);
      showSnackbar('Failed to end meeting: ' + errorMessage, 'error');
    }
  };

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [selectedGroupId]);

  const loadInitialData = async () => {
  try {
    setLoading(true);
    setError(null);

    // Load groups and officers
    const [groupsResponse, officersResponse] = await Promise.all([
      diaryAPI.utils.getGroups(),
      diaryAPI.utils.getOfficers()
    ]);

    const allGroups = groupsResponse.results || groupsResponse || [];
    const availableOfficers = officersResponse.results || officersResponse;

    // Filter groups based on selection
    const filteredGroups = selectedGroupIds && selectedGroupIds.length > 0
      ? allGroups.filter(group => selectedGroupIds.includes(group.id))
      : selectedGroupId 
        ? allGroups.filter(group => group.id === selectedGroupId)
        : allGroups;

    // Fetch meetings for each group in parallel
    const groupsWithMeetings = await Promise.all(
      filteredGroups.map(async group => {
        try {
          // Fetch meetings specifically for this group
          const meetings = await diaryAPI.meetings.getGroupMeetings(group.id);
          
          // Sort meetings by date (most recent first)
          const sortedMeetings = [...meetings].sort((a, b) => 
            new Date(b.meeting_date || b.previous_meeting_date) - 
            new Date(a.meeting_date || a.previous_meeting_date)
          );
          
          const latestMeeting = sortedMeetings[0]; // Most recent meeting
          
          return {
            id: group.id,
            groupName: group.name,
            groupId: `GRP${group.id.toString().padStart(3, '0')}`,
            preferredMeetingDay: group.preferred_meeting_day || latestMeeting?.preferred_meeting_day || 'Not set',
            preferredWeek: group.preferred_week || latestMeeting?.preferred_week || 'Not set',
            status: latestMeeting?.scheduled_status || 'unscheduled',
            previousMeetingDate: latestMeeting?.previous_meeting_date || null,
            daysSinceLastMeeting: calculateDaysSince(latestMeeting?.previous_meeting_date),
            nextMeetingOfficers: latestMeeting?.next_meeting_officers || [],
            totalBudget: latestMeeting?.total_budget || 0,
            hasActiveMeeting: !!latestMeeting,
            activeMeetingId: latestMeeting?.id || null,
            meetings: sortedMeetings.map(meeting => ({
              id: meeting.id,
              date: meeting.meeting_date,
              period_start: meeting.period_start,
              approvedLoans: meeting.approved_loans_from_office || 0,
              additionalFunds: meeting.requested_advance_loans || 0,
              meetingStatus: meeting.meeting_status,
              otherExpenses: meeting.total_expenses || 0
            }))
          };
        } catch (err) {
          console.error(`Failed to load meetings for group ${group.id}:`, err);
          return {
            id: group.id,
            groupName: group.name,
            groupId: `GRP${group.id.toString().padStart(3, '0')}`,
            status: 'Error',
            meetings: []
          };
        }
      })
    );

    setGroupsData(groupsWithMeetings);
    setAvailableOfficers(availableOfficers);
    
  } catch (err) {
    const errorMessage = handleAPIError(err);
    setError(errorMessage);
    showSnackbar('Failed to load data: ' + errorMessage, 'error');
  } finally {
    setLoading(false);
  }
};

  const calculateDaysSince = (dateString) => {
    if (!dateString) return 0;
    const now = new Date();
    const pastDate = new Date(dateString);
    const diffTime = now - pastDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'success';
      case 'unscheduled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getMeetingStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'ongoing':
        return 'info';
      case 'ended':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Handle reschedule
  const handleReschedule = (groupId) => {
    const group = groupsData.find(g => g.id === groupId);
    const isScheduling = !group.hasActiveMeeting;
    
    setRescheduleForm({
      rescheduleDate: true,
      reassignOfficers: false,
      newDate: group?.meetings[0]?.date || '',
      selectedOfficers: group?.nextMeetingOfficers.map(o => o.name) || []
    });
    setRescheduleDialog({ open: true, groupId, isScheduling });
  };

  const handleRescheduleSubmit = async () => {
    const { rescheduleDate, reassignOfficers, newDate, selectedOfficers } = rescheduleForm;
    const group = groupsData.find(g => g.id === rescheduleDialog.groupId);
    const isScheduling = rescheduleDialog.isScheduling;
    
    if (isScheduling) {
      // For scheduling new meetings, date is required
      if (!newDate) {
        showSnackbar('Please select a meeting date.', 'warning');
        return;
      }
    } else {
      // For rescheduling, at least one option must be selected
      if (!rescheduleDate && !reassignOfficers) {
        showSnackbar('Please select at least one option to reschedule.', 'warning');
        return;
      }
      if (rescheduleDate && !newDate) {
        showSnackbar('Please select a new date.', 'warning');
        return;
      }
    }
  
    if (reassignOfficers && selectedOfficers.length === 0) {
      showSnackbar('Please select at least one officer.', 'warning');
      return;
    }
  
    try {
      if (isScheduling) {
        // Create new meeting
        const meetingData = {
          group: rescheduleDialog.groupId,
          meeting_date: newDate,
          officer_ids: reassignOfficers ? selectedOfficers.map(name => {
            const officer = availableOfficers.find(o => 
              `${o.first_name} ${o.last_name}`.trim() === name || o.username === name
            );
            return officer?.id;
          }).filter(Boolean) : undefined
        };
  
        await diaryAPI.meetings.createMeeting(meetingData);
        showSnackbar('Meeting scheduled successfully!', 'success');
      } else {
        // Reschedule existing meeting
        const rescheduleData = {
          reschedule_date: rescheduleDate,
          reassign_officers: reassignOfficers,
          new_date: rescheduleDate ? newDate : undefined,
          officer_ids: reassignOfficers ? selectedOfficers.map(name => {
            const officer = availableOfficers.find(o => 
              `${o.first_name} ${o.last_name}`.trim() === name || o.username === name
            );
            return officer?.id;
          }).filter(Boolean) : undefined
        };
  
        await diaryAPI.meetings.rescheduleMeeting(group.activeMeetingId, rescheduleData);
        showSnackbar('Meeting rescheduled successfully!', 'success');
      }
      
      // Refresh data and close dialog
      await loadInitialData();
      setRescheduleDialog({ open: false, groupId: null, isScheduling: false });
      setRescheduleForm({
        rescheduleDate: false,
        reassignOfficers: false,
        newDate: '',
        selectedOfficers: []
      });
      
    } catch (err) {
      const errorMessage = handleAPIError(err);
      const action = isScheduling ? 'schedule' : 'reschedule';
      showSnackbar(`Failed to ${action} meeting: ${errorMessage}`, 'error');
    }
  };

  // Handle advance
  const handleAddAdvance = (groupId) => {
    setAdvanceAmount('');
    setAdvanceDialog({ open: true, groupId });
  };

  const handleAdvanceSubmit = async () => {
    if (!advanceAmount || parseFloat(advanceAmount) <= 0) {
      showSnackbar('Please enter a valid advance amount.', 'warning');
      return;
    }
  
    try {
      // ✅ FIXED: Get the group data and use activeMeetingId instead of groupId
      const group = groupsData.find(g => g.id === advanceDialog.groupId);
      
      if (!group?.activeMeetingId) {
        showSnackbar('No active meeting found for this group.', 'error');
        return;
      }
  
      // Use the meeting ID, not the group ID
      await diaryAPI.meetings.addAdvance(group.activeMeetingId, parseFloat(advanceAmount));
      
      // Refresh data
      await loadInitialData();
      
      setAdvanceDialog({ open: false, groupId: null });
      setAdvanceAmount('');
      
      showSnackbar('Advance funds added successfully!', 'success');
      
    } catch (err) {
      const errorMessage = handleAPIError(err);
      showSnackbar('Failed to add advance: ' + errorMessage, 'error');
    }
  };

  // Handle expenses
  const handleAddExpenses = (groupId) => {
    setExpensesList([{ name: '', amount: '' }]);
    setExpensesDialog({ open: true, groupId });
  };

  const addExpenseRow = () => {
    setExpensesList([...expensesList, { name: '', amount: '' }]);
  };

  const removeExpenseRow = (index) => {
    if (expensesList.length > 1) {
      setExpensesList(expensesList.filter((_, i) => i !== index));
    }
  };

  const updateExpense = (index, field, value) => {
    const updated = expensesList.map((expense, i) => 
      i === index ? { ...expense, [field]: value } : expense
    );
    setExpensesList(updated);
  };

  const handleExpensesSubmit = async () => {
    const validExpenses = expensesList.filter(exp => 
      exp.name.trim() && exp.amount && parseFloat(exp.amount) > 0
    );
    
    if (validExpenses.length === 0) {
      showSnackbar('Please add at least one valid expense.', 'warning');
      return;
    }
  
    try {
      // ✅ FIXED: Get the group data and use activeMeetingId instead of groupId
      const group = groupsData.find(g => g.id === expensesDialog.groupId);
      
      if (!group?.activeMeetingId) {
        showSnackbar('No active meeting found for this group.', 'error');
        return;
      }
  
      const expensesData = validExpenses.map(exp => ({
        expense_name: exp.name,
        amount: parseFloat(exp.amount),
        description: exp.description || ''
      }));
  
      // Use the meeting ID, not the group ID
      await diaryAPI.meetings.addMeetingExpenses(group.activeMeetingId, expensesData);
      
      // Refresh data
      await loadInitialData();
      
      setExpensesDialog({ open: false, groupId: null });
      setExpensesList([{ name: '', amount: '' }]);
      
      showSnackbar('Expenses added successfully!', 'success');
      
    } catch (err) {
      const errorMessage = handleAPIError(err);
      showSnackbar('Failed to add expenses: ' + errorMessage, 'error');
    }
  };
  

  const handleOfficerToggle = (officerName) => {
    setRescheduleForm(prev => ({
      ...prev,
      selectedOfficers: prev.selectedOfficers.includes(officerName)
        ? prev.selectedOfficers.filter(name => name !== officerName)
        : [...prev.selectedOfficers, officerName]
    }));
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading meetings...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadInitialData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
      <Button
  variant="outlined"
  color="primary"
  onClick={onBack}
  sx={{
    fontWeight: 500,
    textTransform: 'none',
    px: 3,
    py: 1,
  }}
>
  {selectedGroupIds?.length > 0 
    ? `← Back to Calendar` 
    : selectedGroupId 
      ? `← Back to All Groups` 
      : '← Back to Groups'}
</Button>
      </Box>

      {/* Groups Cards */}
      <Grid container spacing={3}>
        {groupsData.map((group) => (
          <Grid item xs={12} lg={6} key={group.id}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Card Header */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 3,
                    background: "linear-gradient(90deg, rgba(25,118,210,1) 0%, rgba(236,26,35,1) 51%, rgba(167,206,53,1) 100%)",
                    padding: 2,
                    borderRadius: 1
                  }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff' }}>
                    {group.groupName}
                  </Typography>
                  <Chip
                    label={group.groupId}
                    variant="outlined"
                    sx={{ fontWeight: 500, borderColor: '#1976d2', color: '#fff' }}
                  />
                </Box>

                {/* Group Details */}
                <Grid container spacing={2} sx={{ mb: 3, alignItems: 'flex-start' }}>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday sx={{ fontSize: 18, color: '#666' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Preferred Meeting Day
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {group.preferredMeetingDay}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ fontSize: 18, color: '#666' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Preferred Week
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {group.preferredWeek}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Status
                      </Typography>
                      <Chip
                        label={group.status}
                        color={getStatusColor(group.status)}
                        size="small"
                        sx={{ mt: 0.5, fontWeight: 500 }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Previous Meeting
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(group.previousMeetingDate)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Days Since Last Meeting
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color={group.daysSinceLastMeeting > 30 ? 'error.main' : 'text.primary'}>
                        {group.daysSinceLastMeeting < 0 ? 'Upcoming' : `${group.daysSinceLastMeeting} days`}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ mb: 1, minHeight: 48 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Officer(s) for Next Meeting
                      </Typography>
                      {group.nextMeetingOfficers.length > 0 ? (
                        <AvatarGroup max={3} sx={{ 
                          mt: 0.5, 
                          minHeight: 24,
                          '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 10 } 
                        }}>
                          {group.nextMeetingOfficers.map((officer, index) => (
                            <Tooltip key={index} title={officer.name}>
                              <Avatar sx={{ bgcolor: '#1976d2' }}>
                                {officer.avatar}
                              </Avatar>
                            </Tooltip>
                          ))}
                        </AvatarGroup>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ 
                          minHeight: 24, 
                          display: 'flex', 
                          alignItems: 'center',
                          mt: 0.5
                        }}>
                          Not assigned
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* Total Budget */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, p: 2, backgroundColor: theme.palette.background.default, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalance sx={{ fontSize: 20, color: '#2e7d32' }} />
                    <Typography variant="body2" color="text.secondary">
                      Total Budget (cash from office):
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    KES {group.totalBudget.toLocaleString()}
                  </Typography>
                </Box>

                {/* Action Buttons */}
                <Grid container spacing={1} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<Event />}
                      onClick={() => handleReschedule(group.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      {group.hasActiveMeeting ? 'Reschedule' : 'Schedule'}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<TrendingUp />}
                      onClick={() => handleAddAdvance(group.id)}
                      disabled={!group.hasActiveMeeting}
                      sx={{ 
                        textTransform: 'none', 
                        color: group.hasActiveMeeting ? '#2e7d32' : 'text.disabled', 
                        borderColor: group.hasActiveMeeting ? '#2e7d32' : 'text.disabled' 
                      }}
                    >
                      Add Advance
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddExpenses(group.id)}
                      disabled={!group.hasActiveMeeting}
                      sx={{ textTransform: 'none' }}
                    >
                      Add Expenses
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={() => handleEndMeeting(group)}
                      disabled={
                        !group.hasActiveMeeting || 
                        !group.meetings?.[0]?.date || 
                        group.meetings[0]?.meetingStatus !== 'ongoing'
                      }
                      sx={{ 
                        textTransform: 'none',
                        backgroundColor: '#d32f2f',
                        '&:hover': {
                          backgroundColor: '#b71c1c'
                        },
                        '&:disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.12)'
                        }
                      }}
                    >
                      End Meeting
                    </Button>
                  </Grid>
                </Grid>

                {/* Scheduled Meetings Table */}
                {group.meetings.length > 0 && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                      Scheduled Meeting
                    </Typography>
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#2b323c' : '#FAFAFA' }}>
                          <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Meeting Date</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Meeting Status</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Approved Loans</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Advance Funds</TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Other Expenses</TableCell>
                        </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.meetings.map((meeting) => (
                            <TableRow key={meeting.id}>
                              <TableCell>{formatDate(meeting.date)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={meeting.meetingStatus}
                                  color={getMeetingStatusColor(meeting.meetingStatus)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                KES {meeting.approvedLoans.toLocaleString()}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                KES {meeting.additionalFunds.toLocaleString()}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500 }}>
                                KES {meeting.otherExpenses.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Reschedule Dialog */}
      <Dialog 
  open={rescheduleDialog.open} 
  onClose={() => setRescheduleDialog({ open: false, groupId: null, isScheduling: false })}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    {rescheduleDialog.isScheduling ? 'Schedule Meeting' : 'Reschedule Meeting'}
  </DialogTitle>
  <DialogContent>
    <Box sx={{ pt: 2 }}>
      {!rescheduleDialog.isScheduling && (
        <FormControlLabel
          control={
            <Checkbox
              checked={rescheduleForm.rescheduleDate}
              onChange={(e) => setRescheduleForm(prev => ({ ...prev, rescheduleDate: e.target.checked }))}
            />
          }
          label="Reschedule meeting date"
        />
      )}
      
      {(rescheduleForm.rescheduleDate || rescheduleDialog.isScheduling) && (
        <Box sx={{ ml: rescheduleDialog.isScheduling ? 0 : 4, mb: 2 }}>
          <TextField
            type="date"
            label={rescheduleDialog.isScheduling ? "Meeting Date" : "New Meeting Date"}
            value={rescheduleForm.newDate}
            onChange={(e) => setRescheduleForm(prev => ({ ...prev, newDate: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={rescheduleForm.reassignOfficers}
            onChange={(e) => setRescheduleForm(prev => ({ ...prev, reassignOfficers: e.target.checked }))}
          />
        }
        label={rescheduleDialog.isScheduling ? "Assign officers" : "Reassign officers"}
      />
            {rescheduleForm.reassignOfficers && (
              <Box sx={{ ml: 4, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Officers:</Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {availableOfficers.map((officer) => {
                    const officerName = `${officer.first_name || ''} ${officer.last_name || ''}`.trim() || officer.username;
                    return (
                      <FormControlLabel
                        key={officer.id}
                        control={
                          <Checkbox
                            checked={rescheduleForm.selectedOfficers.includes(officerName)}
                            onChange={() => handleOfficerToggle(officerName)}
                          />
                        }
                        label={officerName}
                        sx={{ display: 'block' }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialog({ open: false, groupId: null, isScheduling: false })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleRescheduleSubmit}>
            {rescheduleDialog.isScheduling ? 'Schedule Meeting' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Advance Dialog */}
      <Dialog 
        open={advanceDialog.open} 
        onClose={() => setAdvanceDialog({ open: false, groupId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Advance Funds</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Advance Amount"
              type="number"
              value={advanceAmount}
              onChange={(e) => setAdvanceAmount(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>KES</Typography>,
              }}
              placeholder="Enter amount"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvanceDialog({ open: false, groupId: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAdvanceSubmit}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Expenses Dialog */}
      <Dialog 
        open={expensesDialog.open} 
        onClose={() => setExpensesDialog({ open: false, groupId: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Additional Expenses</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {expensesList.map((expense, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="Expense Name"
                  value={expense.name}
                  onChange={(e) => updateExpense(index, 'name', e.target.value)}
                  sx={{ flex: 2 }}
                  placeholder="e.g., Transport, Stationery"
                />
                <TextField
                  label="Amount"
                  type="number"
                  value={expense.amount}
                  onChange={(e) => updateExpense(index, 'amount', e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>KES</Typography>,
                  }}
                  placeholder="0"
                />
                {expensesList.length > 1 && (
                  <IconButton 
                    onClick={() => removeExpenseRow(index)}
                    color="error"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}
            
            <Button
              startIcon={<Add />}
              onClick={addExpenseRow}
              sx={{ mt: 1, textTransform: 'none' }}
              variant="outlined"
            >
              Add Another Expense
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpensesDialog({ open: false, groupId: null })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleExpensesSubmit}>
            Add Expenses
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Meeting Dialog */}
      <EndMeeting
        open={endMeetingDialog.open}
        onClose={() => setEndMeetingDialog({ open: false, groupData: null, advanceFromOffice: 0 })}
        meeting={endMeetingDialog.groupData}
        onEndMeeting={handleEndMeetingSubmit}
        loading={loading}
        advanceFromOffice={endMeetingDialog.advanceFromOffice || 0}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DetailedInfo;